import type {
  FieldResults_AthleteResult,
  FieldResults_AthleteResultsMap,
  SocketFieldResultsResult,
  SocketResultMessagePayloadFieldResults
} from '@/components/results/socket/socket-field-results/socket-field-results-models'
import type {
  LiveFeedMessageBase,
  LiveFeedMessageFieldResult,
  LiveFeedMessageManualEntry,
  LiveFeedMessageTrackResult,
  LiveFeedMessageType,
  SocketResultSummaryField
} from '@/components/results/socket/live-feed/live-feed-models'
import type { CompEvent, TrialNo } from '@/components/results/store-comp-results'
import { cleanUpScoreValueForDisplay } from '@/components/results/results-service'
import { createGuid } from '@/services/common-service-utils'
import type { SocketsResultsPfPayload } from '@/components/results/socket/socket-results-pf/sockets-results-pf-models'
import type {
  ResultSocketActionType,
  SocketResultData
} from '@/components/results/socket/result-socket-models'
import type { SocketResultMessagePayloadResultsManual } from '@/components/results/socket/socket-results-manual/socket-results-manual-models'
import type { LiveFeedSocketMessage } from '@/components/results/socket/live-feed/models/live-feed-models'
import type { SocketResultMessagePayloadFieldNextup } from '@/components/results/socket/models/field-nextup'

export function getFieldResultsAthletes(
  socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults
): FieldResults_AthleteResultsMap {
  const eventNo: number = socketResultMessagePayloadFieldResults.ranking.eventGroup.eventNo
  return socketResultMessagePayloadFieldResults[eventNo]
}

export function getCurrentTrial(athleteResult: FieldResults_AthleteResult): {
  scoreValue: string
  trialNo: TrialNo | ''
} {
  let scoreValue: string = ''
  let trialNo: TrialNo | '' = ''
  const trailMapKeys: TrialNo[] = ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8']
  scoreValue = trailMapKeys.reduce((acc, key) => {
    if (athleteResult[key]) {
      trialNo = key
      acc = athleteResult[key] ? cleanUpScoreValueForDisplay(athleteResult[key]!.toString()) : ''
    }
    return acc
  }, '')
  return { scoreValue, trialNo }
}

export function mapSocketToLiveFeedMessageField(
  socketResultMessagePayloadFieldResults: SocketResultMessagePayloadFieldResults
): LiveFeedMessageFieldResult | undefined {
  const eventNo: number = socketResultMessagePayloadFieldResults.ranking.eventGroup.eventNo

  //  Now we can get the property off the object
  const athletesRecord: FieldResults_AthleteResultsMap =
    socketResultMessagePayloadFieldResults[eventNo]
  const athletes: FieldResults_AthleteResult[] = Object.values(athletesRecord)
  const athleteIds: (string | number)[] = Object.keys(athletesRecord)

  //  If greater than 1, the Live Feed will keep displaying the previous results.
  if (athletes.length > 1) {
    return undefined
  }

  const athleteResult = athletes.length > 0 ? athletes[0] : undefined
  const athleteId: number | -1 =
    athleteIds.length > 0
      ? typeof athleteIds[0] === 'string'
        ? Number(athleteIds[0])
        : athleteIds[0]
      : -1

  let scoreValue: string = ''
  let trialNo: TrialNo | '' = ''
  if (athleteResult) {
    const currentTrial = getCurrentTrial(athleteResult)
    scoreValue = currentTrial.scoreValue
    trialNo = currentTrial.trialNo
    // const trailMapKeys: TrialNo[] = ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8']
    // scoreValue = trailMapKeys.reduce((acc, key) => {
    //   if (athleteResult[key]) {
    //     trialNo = key
    //     acc = athleteResult[key] ? cleanUpScoreValueForDisplay(athleteResult[key]!.toString()) : ''
    //   }
    //   return acc
    // }, '')
  }

  let socketAthleteResult: SocketFieldResultsResult | undefined = undefined
  const heats = socketResultMessagePayloadFieldResults.ranking.heats
  let heatSummary: SocketResultSummaryField[] = []
  if (heats.length > 0) {
    const results = heats[0].results
    if (results.length > 0) {
      //  loop through the results and find the athlete
      socketAthleteResult = results.find((result) => result.athleteId === athleteId)
    }
    const heat1 = heats[0]
    heatSummary = heat1.results.map((result) => {
      return {
        position: result.position,
        athlete: result.athlete,
        athleteId: result.athleteId,
        clubName: result.clubName,
        bibNo: result.bibNo,
        scoreValue: result.scoreValue
      }
    })
  }

  let athleteTrials = {}
  if (socketResultMessagePayloadFieldResults.ranking.trials) {
    athleteTrials = socketResultMessagePayloadFieldResults.ranking.trials[athleteId]
  }

  return {
    eventGroup: {
      id: socketResultMessagePayloadFieldResults.ranking.eventGroup.id,
      name: socketResultMessagePayloadFieldResults.ranking.eventGroup.name,
      eventNo: socketResultMessagePayloadFieldResults.ranking.eventGroup.eventNo
    },
    latest: {
      position: socketAthleteResult ? socketAthleteResult.position.toString() : '',
      athleteId: athleteId,
      currentTrial: {
        trialNo: trialNo,
        score: scoreValue
      },
      athleteResult,
      trial: athleteTrials,
      athletesResults: athletes,
      bibNo: socketAthleteResult ? socketAthleteResult.bibNo.toString() : '',
      scoreValue: scoreValue
    },
    trials: socketResultMessagePayloadFieldResults.ranking.trials
      ? socketResultMessagePayloadFieldResults.ranking.trials
      : {},
    heatSummary: heatSummary
  }
}

export function mapSocketToLiveFeedMessagePhoto(
  socketsResultsPfPayload: SocketsResultsPfPayload
): LiveFeedMessageTrackResult {
  return {
    eventGroup: {
      id: socketsResultsPfPayload.eventGroup.id,
      name: socketsResultsPfPayload.eventGroup.name,
      eventNo: socketsResultsPfPayload.eventGroup.eventNo,
      typeNo: socketsResultsPfPayload.eventGroup.typeNo
    },
    heatSummary: socketsResultsPfPayload.results.map((result) => {
      return {
        position: result.options.place,
        athlete: result.options.athlete,
        athleteId: result.options.athleteId,
        clubName: result.options.club,
        bibNo: result.options.bibNo,
        scoreValue: result.resultvalue
      }
    })
  }
}

export function mapSocketToLiveFeedManualEntry(
  socketResultMessagePayloadResultsManual: SocketResultMessagePayloadResultsManual
): LiveFeedMessageManualEntry {
  // get last heat
  const lastHeat =
    socketResultMessagePayloadResultsManual.heats[
      socketResultMessagePayloadResultsManual.heats.length - 1
    ]

  return {
    eventGroup: {
      id: socketResultMessagePayloadResultsManual.eventGroup.id,
      name: socketResultMessagePayloadResultsManual.eventGroup.name,
      eventNo: socketResultMessagePayloadResultsManual.eventGroup.eventNo
    },
    heatSummary: lastHeat.results.map((result) => {
      return {
        position: result.position,
        athlete: result.athlete,
        athleteId: result.athleteId,
        clubName: result.clubName,
        bibNo: result.bibNo,
        scoreValue: result.scoreValue
      }
    })
  }
}

export function factoryLiveFeedMessageField(
  resultSocketActionType: ResultSocketActionType,
  payload:
    | SocketsResultsPfPayload
    | SocketResultMessagePayloadFieldResults
    | SocketResultMessagePayloadResultsManual
): LiveFeedMessageBase | undefined {
  // Create displayTime in format 'HH:mm:ss'
  const displayTime = new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })

  const payLoadCreationMap: Partial<
    Record<
      ResultSocketActionType,
      {
        type: LiveFeedMessageType
        creator: Function
      }
    >
  > = {
    'results-manual': {
      type: 'MANUAL_ENTRY',
      creator: mapSocketToLiveFeedManualEntry
    },
    'field-results': {
      type: 'FIELD_RESULT',
      creator: mapSocketToLiveFeedMessageField
    },
    photofinish: {
      type: 'TRACK_RESULT',
      creator: mapSocketToLiveFeedMessagePhoto
    }
  }

  const payLoadCreation = payLoadCreationMap[resultSocketActionType]

  if (!payLoadCreation) {
    throw new Error(`Unknown ResultSocketActionType: ${resultSocketActionType}`)
  }

  const liveFeedMessageType: LiveFeedMessageType = payLoadCreation.type
  const createdPayload = payLoadCreation.creator(payload)

  return {
    guid: createGuid(),
    time: new Date().toISOString(),
    displayTime: displayTime,
    type: liveFeedMessageType,
    payload: createdPayload
  }
}

export function convertSocketResultDataToLiveFeedMessage(
  socketResultData: SocketResultData<unknown>,
  compEvent: CompEvent | undefined
): LiveFeedSocketMessage {
  let uniqueKey = socketResultData.key
  if (!uniqueKey) {
    uniqueKey = createGuid()
  }

  // get time from socketResultData
  const time = socketResultData.utcTime

  const liveFeedSocketMessage: LiveFeedSocketMessage = {
    guid: uniqueKey,
    time: time,
    displayTime: new Date(time).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }),
    type: 'FIELD_RESULT',
    action: socketResultData.action,
    payload: null,
    title: ''
  }

  if (socketResultData.action === 'field-results') {
    liveFeedSocketMessage.payload = mapSocketToLiveFeedMessageField(
      socketResultData.payload as SocketResultMessagePayloadFieldResults
    ) as LiveFeedMessageFieldResult
  }

  if (socketResultData.action === 'photofinish') {
    liveFeedSocketMessage.payload = mapSocketToLiveFeedMessagePhoto(
      socketResultData.payload as SocketsResultsPfPayload
    ) as LiveFeedMessageTrackResult
  }

  if (socketResultData.action === 'results-manual') {
    liveFeedSocketMessage.payload = mapSocketToLiveFeedManualEntry(
      socketResultData.payload as SocketResultMessagePayloadResultsManual
    ) as LiveFeedMessageManualEntry
  }

  if (socketResultData.action === 'field-nextup') {
    liveFeedSocketMessage.payload =
      socketResultData.payload as SocketResultMessagePayloadFieldNextup

    // These don't come up with typeNo: T1, T2...F12, etc
    if (compEvent) {
      liveFeedSocketMessage.title =
        compEvent.typeNo + ' : ' + liveFeedSocketMessage.payload.event.name
    }
  }

  return liveFeedSocketMessage
}
